/**
 * Privacy Policy Page - Olympic Fit Wellness
 */
import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <div className="bg-white" data-testid="privacy-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">LEGAL</span>
          <h1 className="text-4xl md:text-5xl font-heading font-normal text-white mb-4 tracking-[0.04em] uppercase">
            Privacy Policy
          </h1>
          <p className="text-neutral-400">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto prose prose-neutral">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">1. Introduction</h2>
                <p className="text-neutral-600 leading-relaxed">
                  Olympic Fit Wellness ("OFW," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our facilities, use our mobile application, or interact with our website.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">2. Information We Collect</h2>
                <p className="text-neutral-600 leading-relaxed mb-4">We collect information you provide directly to us, including:</p>
                <ul className="list-disc list-inside text-neutral-600 space-y-2">
                  <li>Personal identification information (name, email, phone number, address)</li>
                  <li>Payment information (credit card details processed through secure third-party processors)</li>
                  <li>Health and fitness information (workout logs, nutrition data, health goals)</li>
                  <li>Photos and videos (if you choose to share them)</li>
                  <li>Communications (emails, messages, feedback)</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">3. How We Use Your Information</h2>
                <p className="text-neutral-600 leading-relaxed mb-4">We use the information we collect to:</p>
                <ul className="list-disc list-inside text-neutral-600 space-y-2">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process transactions and manage your membership</li>
                  <li>Personalize your wellness experience</li>
                  <li>Communicate with you about services, promotions, and events</li>
                  <li>Monitor and analyze usage trends</li>
                  <li>Ensure safety and security at our facilities</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">4. Information Sharing</h2>
                <p className="text-neutral-600 leading-relaxed">
                  We do not sell your personal information. We may share your information with third-party service providers who perform services on our behalf, such as payment processing, data analysis, email delivery, and marketing assistance. All third parties are contractually obligated to keep your information confidential.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">5. Data Security</h2>
                <p className="text-neutral-600 leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">6. Your Rights</h2>
                <p className="text-neutral-600 leading-relaxed mb-4">You have the right to:</p>
                <ul className="list-disc list-inside text-neutral-600 space-y-2">
                  <li>Access and receive a copy of your personal data</li>
                  <li>Rectify inaccurate personal data</li>
                  <li>Request deletion of your personal data</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Data portability</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">7. Cookies and Tracking</h2>
                <p className="text-neutral-600 leading-relaxed">
                  We use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or indicate when a cookie is being sent.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">8. Children's Privacy</h2>
                <p className="text-neutral-600 leading-relaxed">
                  Our services are not directed to individuals under 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">9. Changes to This Policy</h2>
                <p className="text-neutral-600 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">10. Contact Us</h2>
                <p className="text-neutral-600 leading-relaxed">
                  If you have questions about this Privacy Policy, please contact us at:
                </p>
                <div className="mt-4 p-6 bg-neutral-50">
                  <p className="text-neutral-700"><strong>Olympic Fit Wellness</strong></p>
                  <p className="text-neutral-600">Privacy Team</p>
                  <p className="text-neutral-600">1100 N Central Expy, McKinney, TX 75070</p>
                  <p className="text-neutral-600">Email: privacy@olympicfitwellness.com</p>
                  <p className="text-neutral-600">Phone: (469) 555-0100</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 border-t border-neutral-200">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <Link to="/" className="text-neutral-600 hover:text-neutral-900 transition-colors">
            ← Back to Home
          </Link>
        </div>
      </section>
    </div>
  );
}
